import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Switch from "react-switch"; // Import the React Switch component
import * as Yup from "yup";
import { useFormik } from "formik";
import { fetchData, uploadFile, createEvent } from "../../services/apiServices";
import { useDropzone } from "react-dropzone"; // For drag and drop functionality
import Cropper from "react-easy-crop"; // Replacing react-image-crop with react-easy-crop
import { useNavigate } from "react-router-dom";

// Helper to get current UTC datetime in the correct format
const getCurrentUTCDateTimeLocal = () => {
  const now = new Date();
  // Use UTC methods to get the current UTC time without timezone conversion
  const pad = (n) => n.toString().padStart(2, "0");
  const yyyy = now.getUTCFullYear();
  const mm = pad(now.getUTCMonth() + 1);
  const dd = pad(now.getUTCDate());
  const hh = pad(now.getUTCHours());
  const min = pad(now.getUTCMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}:00`;
};

// Helper to normalize a datetime-local string to force seconds to 00
const normalizeDateTimeLocal = (dt) => {
  if (!dt) return dt;
  // If already has seconds, replace them with 00
  if (dt.length === 19) return dt.slice(0, 17) + "00";
  // If only yyyy-MM-ddTHH:mm, add :00
  if (dt.length === 16) return dt + ":00";
  return dt;
};

// Format date in UTC format for API calls
const formatDateForUTC = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString(); // Return ISO string in UTC
};

// Helper function to safely convert any value to a trimmed string
const safeStringTrim = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  return value.toString().trim();
};

// Image Cropper Modal Component
const ImageCropperModal = ({ isOpen, toggle, image, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // When modal opens, reset the crop state
  useEffect(() => {
    if (isOpen) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPreviewUrl(null);
    }
  }, [isOpen]);

  // Handle crop complete
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onCropCompleteCallback = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
    generatePreview(croppedAreaPixels);
  }, []);

  // Generate a preview from the crop selection
  const generatePreview = useCallback(
    async (croppedAreaPixels) => {
      if (!croppedAreaPixels || !image) return;

      try {
        const imageObj = new Image();
        imageObj.src = image;

        await new Promise((resolve) => {
          imageObj.onload = resolve;
        });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set canvas dimensions to the cropped size
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
          imageObj,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        // Convert to data URL for preview
        const dataUrl = canvas.toDataURL("image/jpeg");
        setPreviewUrl(dataUrl);
      } catch (error) {
        console.error("Error generating preview:", error);
      }
    },
    [image]
  );

  // Create a crop preview and convert to blob for saving
  const createCropPreview = async () => {
    setLoading(true);

    try {
      if (!croppedAreaPixels || !image) {
        throw new Error("No valid crop selection");
      }

      const imageObj = new Image();
      imageObj.src = image;

      await new Promise((resolve) => {
        imageObj.onload = resolve;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size to exactly 256x256px for events
      canvas.width = 256;
      canvas.height = 256;

      ctx.drawImage(
        imageObj,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        256, // Target width
        256 // Target height
      );

      // Convert to blob
      return new Promise((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error("Canvas is empty");
              return;
            }
            resolve(blob);
          },
          "image/jpeg",
          1
        );
      });
    } catch (error) {
      console.error("Error creating crop preview:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCrop = async () => {
    if (!croppedAreaPixels) {
      alert("Please make a crop selection first");
      return;
    }

    try {
      setLoading(true);
      const croppedImageBlob = await createCropPreview();
      onCropComplete(croppedImageBlob);
      toggle();
    } catch (error) {
      alert("Error processing the image. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Crop Image (Final size: 256x256px)
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="8">
            <div
              className="crop-container"
              style={{ height: "400px", position: "relative" }}
            >
              {image ? (
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onCropComplete={onCropCompleteCallback}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  showGrid={true}
                />
              ) : (
                <div className="text-center p-4 border rounded">
                  <p>No image loaded. Please select an image first.</p>
                </div>
              )}
            </div>
            <div className="controls mt-3">
              <div className="d-flex align-items-center">
                <span className="me-2">Zoom</span>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="form-range"
                />
              </div>
              <div className="text-muted mt-2">
                {image
                  ? "Use the slider to zoom, drag to reposition, then click Apply Crop"
                  : "Image will appear here for cropping once selected."}
              </div>
            </div>
          </Col>
          <Col md="4">
            <div className="preview-container mt-3">
              <h6>Preview:</h6>
              {previewUrl ? (
                <div
                  className="square-preview"
                  style={{
                    width: "150px",
                    height: "150px",
                    overflow: "hidden",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt="Crop preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #ced4da",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span className="text-muted">No preview</span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSaveCrop}
          disabled={loading || !croppedAreaPixels}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Processing...
            </>
          ) : (
            "Apply Crop"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// Region multi-select with chips component
const RegionMultiSelect = ({ regions, selectedRegions, onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  const handleRegionSelect = (regionId) => {
    const isSelected = selectedRegions.includes(regionId);
    let newSelectedRegions;

    if (isSelected) {
      // Remove the region if already selected
      newSelectedRegions = selectedRegions.filter((id) => id !== regionId);
    } else {
      // Add the region if not selected
      newSelectedRegions = [...selectedRegions, regionId];
    }

    onChange(newSelectedRegions);
  };

  const handleRemoveRegion = (regionId, e) => {
    e.stopPropagation(); // Prevent dropdown toggle
    const newSelectedRegions = selectedRegions.filter((id) => id !== regionId);
    onChange(newSelectedRegions);
  };

  return (
    <div className="region-multiselect">
      <div
        className="form-control d-flex flex-wrap align-items-center"
        onClick={toggleDropdown}
        style={{
          cursor: "pointer",
          minHeight: "40px",
          height: "auto",
          position: "relative",
        }}
      >
        {selectedRegions.length > 0 ? (
          <div className="d-flex flex-wrap" style={{ maxWidth: "95%" }}>
            {selectedRegions.map((regionId) => {
              const region = regions.find((r) => r._id === regionId);
              return region ? (
                <div
                  key={region._id}
                  className="bg-primary text-white px-2 py-1 rounded d-flex align-items-center me-1 mb-1"
                >
                  <span>{region.name}</span>
                  <span
                    className="ms-1"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleRemoveRegion(region._id, e)}
                  >
                    ×
                  </span>
                </div>
              ) : null;
            })}
          </div>
        ) : (
          <div className="text-muted">Select regions...</div>
        )}
        <div className="dropdown-toggle ms-auto">
          <i className="mdi mdi-chevron-down"></i>
        </div>
      </div>

      <div
        className={`dropdown-menu w-100 ${dropdownOpen ? "show" : ""}`}
        style={{
          maxHeight: "200px",
          overflowY: "auto",
          display: dropdownOpen ? "block" : "none",
          width: "100%",
        }}
      >
        {regions.map((region) => (
          <div
            key={region._id}
            className="dropdown-item"
            onClick={() => handleRegionSelect(region._id)}
            style={{ cursor: "pointer" }}
          >
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={selectedRegions.includes(region._id)}
                onChange={() => {}} // Handled by the parent div click
                id={`region-${region._id}`}
              />
              <label
                className="form-check-label"
                htmlFor={`region-${region._id}`}
              >
                {region.name}
              </label>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DraggableImageUpload = ({
  onFileSelect,
  currentImage,
  isUploading,
  uploadError,
  fieldId,
}) => {
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImage || null);

  useEffect(() => {
    if (currentImage) {
      setPreviewUrl(currentImage);
    }
  }, [currentImage]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Instead of uploading right away, show the cropper
      const imageUrl = URL.createObjectURL(file);
      setSelectedFile(imageUrl);
      setShowCropper(true);
    }
  };

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles[0]) {
      const file = acceptedFiles[0];
      // Create a preview and show the cropper
      const imageUrl = URL.createObjectURL(file);
      setSelectedFile(imageUrl);
      setShowCropper(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
    },
    multiple: false,
  });

  const handleCropComplete = (croppedBlob) => {
    // Create a file from the blob
    const croppedFile = new File([croppedBlob], "cropped-image.jpg", {
      type: "image/jpeg",
    });

    // Create a preview URL for the cropped image
    const previewUrl = URL.createObjectURL(croppedBlob);
    setPreviewUrl(previewUrl);

    // Upload the cropped file
    onFileSelect(croppedFile);
  };

  const toggleCropper = () => {
    setShowCropper(!showCropper);
    if (!showCropper) {
      // Clean up the object URL when we close without saving
      URL.revokeObjectURL(selectedFile);
      setSelectedFile(null);
    }
  };

  return (
    <>
      <div
        {...getRootProps()}
        className={`dropzone-container p-3 border rounded ${
          isDragActive ? "border-primary" : ""
        }`}
        style={{
          borderStyle: "dashed",
          cursor: "pointer",
          backgroundColor: isDragActive ? "rgba(0, 123, 255, 0.1)" : "#fafafa",
          minHeight: "120px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <input {...getInputProps()} id={fieldId} disabled={isUploading} />

        {previewUrl ? (
          <div className="text-center">
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxHeight: "100px",
                maxWidth: "100%",
                marginBottom: "10px",
              }}
              className="img-thumbnail"
            />
            <p className="mb-0">Drag & drop a new image or click to replace</p>
          </div>
        ) : (
          <div className="text-center">
            {isDragActive ? (
              <p className="mb-0">Drop the image here...</p>
            ) : (
              <>
                <i
                  className="ri-upload-cloud-line"
                  style={{ fontSize: "2rem" }}
                ></i>
                <p className="mb-0">
                  Drag & drop an image here, or click to select
                </p>
                <p className="small text-muted">
                  (Image will be cropped to 1:1 ratio)
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {isUploading && (
        <div className="mt-2">
          <Spinner size="sm" /> Uploading...
        </div>
      )}

      {uploadError && (
        <Alert color="danger" className="mt-2">
          {uploadError}
        </Alert>
      )}

      {previewUrl && (
        <div className="mt-2 small text-muted">
          Click on the image area again to upload a different image
        </div>
      )}

      <div className="mt-2">
        <Label className="btn btn-outline-secondary btn-sm">
          Browse Files
          <Input
            type="file"
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="image/*"
            disabled={isUploading}
          />
        </Label>
      </div>

      <ImageCropperModal
        isOpen={showCropper}
        toggle={toggleCropper}
        image={selectedFile}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

const NewEvent = ({ grokEvent, onBack }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  // Add autofill state
  const [hasAutofilled, setHasAutofilled] = useState(false);
  const prevGrokEventRef = useRef();

  // Submarket state (do NOT set from grokEvent directly)
  const [subMarkets, setSubMarkets] = useState([]);
  const [hasSubMarket, setHasSubMarket] = useState(false);

  // Add missing state hooks for uploads and submit status
  const [uploadingFiles, setUploadingFiles] = useState({
    event_image: false,
    full_rules_doc: false,
  });
  const [uploadErrors, setUploadErrors] = useState({
    event_image: null,
    full_rules_doc: null,
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    event_image: null,
    full_rules_doc: null,
  });
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });

  // Fetch categories
  useEffect(() => {
    const getCategories = async () => {
      try {
        const response = await fetchData("api/admin/categories");
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCategories();
  }, []);

  // Fetch regions
  useEffect(() => {
    const getRegions = async () => {
      try {
        const response = await fetchData("api/admin/regions");
        if (response && response.data.regions) {
          setRegions(response.data.regions || []);
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
      }
    };
    getRegions();
  }, []);

  // Autofill from grokEvent if provided
  useEffect(() => {
    // If new grokEvent, reset autofill flag
    if (grokEvent !== prevGrokEventRef.current) {
      setHasAutofilled(false);
      prevGrokEventRef.current = grokEvent;
    }
    if (grokEvent && !hasAutofilled) {
      console.log("Auto-filling form with grok event:", grokEvent);
      // Autofill form fields from grokEvent
      const event = grokEvent.event || {};

      console.log("Event data structure:", {
        event_title: typeof event.event_title,
        market_summary: typeof event.market_summary,
        rules_summary: typeof event.rules_summary,
        settlement_sources: typeof event.settlement_sources,
        settlement_sources_value: event.settlement_sources,
      });

      // Create the values object with proper trimming and fallbacks
      const newValues = {
        event_title: safeStringTrim(event.event_title),
        event_image: event.event_image || null,
        market_summary: safeStringTrim(event.market_summary),
        category: event.category_id || "",
        sub_category: event.sub_category_id || "",
        list_date: event.list_date
          ? normalizeDateTimeLocal(event.list_date)
          : getCurrentUTCDateTimeLocal(),
        regions: event.regions || [],
        rules_summary: safeStringTrim(event.rules_summary),
        rules_variables: safeStringTrim(event.rules_variables),
        settlement_sources: safeStringTrim(event.settlement_sources),
        full_rules_doc: event.full_rules_doc || null,
        has_sub_markets:
          Array.isArray(grokEvent.markets) && grokEvent.markets.length > 0,
        start_date: event.start_date
          ? normalizeDateTimeLocal(event.start_date)
          : getCurrentUTCDateTimeLocal(),
        end_date: event.end_date ? normalizeDateTimeLocal(event.end_date) : "",
        can_close_early: event.can_close_early || false,
        settlement_time: event.settlement_time || 24,
        side_1: safeStringTrim(event.side_1) || "Yes",
        side_2: safeStringTrim(event.side_2) || "No",
        bond_amount: event.bond_amount || 50,
        resolution_window: event.resolution_window || 24,
      };

      console.log("New values to set:", newValues);

      // Set values and mark fields as touched to trigger validation
      validation.setValues(newValues);

      // Mark required fields as touched so validation works properly
      const fieldsToTouch = {
        event_title: true,
        market_summary: true,
        category: true,
        list_date: true,
        rules_summary: true,
        settlement_sources: true,
      };

      // If we have an event image from grok, mark it as touched
      if (newValues.event_image) {
        fieldsToTouch.event_image = true;
      }

      // If we have a rules document from grok, mark it as touched
      if (newValues.full_rules_doc) {
        fieldsToTouch.full_rules_doc = true;
      }

      // Mark single market fields as touched if not using sub markets
      if (!newValues.has_sub_markets) {
        fieldsToTouch.start_date = true;
        fieldsToTouch.end_date = true;
      }

      validation.setTouched(fieldsToTouch);

      // Set uploaded files if they exist in grok data
      if (event.event_image || event.full_rules_doc) {
        setUploadedFiles((prev) => ({
          ...prev,
          event_image: event.event_image || prev.event_image,
          full_rules_doc: event.full_rules_doc || prev.full_rules_doc,
        }));
      }

      // Trigger validation after a short delay to ensure state is updated
      setTimeout(() => {
        validation.validateForm().then((errors) => {
          console.log("Validation after autofill - errors:", errors);
          console.log("Form is valid:", validation.isValid);
          console.log("Form values:", validation.values);
        });
      }, 100);

      // Autofill submarkets if present
      if (Array.isArray(grokEvent.markets) && grokEvent.markets.length > 0) {
        setHasSubMarket(true);
        setSubMarkets(
          grokEvent.markets.map((m) => ({
            name: safeStringTrim(m.name),
            event_image: m.event_image || null,
            list_date: m.list_date
              ? normalizeDateTimeLocal(m.list_date)
              : getCurrentUTCDateTimeLocal(),
            start_date: m.start_date
              ? normalizeDateTimeLocal(m.start_date)
              : getCurrentUTCDateTimeLocal(),
            end_date: m.end_date ? normalizeDateTimeLocal(m.end_date) : "",
            can_close_early: m.can_close_early || false,
            settlement_time: m.settlement_time || 24,
            side_1: safeStringTrim(m.side_1) || "Yes",
            side_2: safeStringTrim(m.side_2) || "No",
            bond_amount: m.bond_amount || 50,
            settlement_time: m.settlement_time || 24,
            resolution_window: m.resolution_window || 24,
          }))
        );
      } else {
        setHasSubMarket(false);
        setSubMarkets([]);
      }
      setHasAutofilled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grokEvent, hasAutofilled]);

  // When grokEvent changes, reset autofill flag
  useEffect(() => {
    setHasAutofilled(false);
  }, [grokEvent]);

  // Update subcategories when category changes
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    validation.handleChange(e);

    if (!categoryId) {
      setSubCategories([]);
      return;
    }

    // Find the selected category and its subcategories
    const selectedCategory = categories.find((cat) => cat._id === categoryId);
    if (selectedCategory && selectedCategory.subcategories) {
      setSubCategories(selectedCategory.subcategories);
    } else {
      setSubCategories([]);
    }
  };

  const handleFileUpload = async (file, field, submarketIndex = null) => {
    if (!file) return;

    const uploadField =
      submarketIndex !== null ? `submarket_${submarketIndex}` : field;

    try {
      setUploadingFiles((prev) => ({ ...prev, [uploadField]: true }));
      setUploadErrors((prev) => ({ ...prev, [uploadField]: null }));

      const fileType = field === "full_rules_doc" ? "document" : "image";
      const fileUrl = await uploadFile(file, fileType);

      if (submarketIndex !== null) {
        const newSubMarkets = [...subMarkets];
        newSubMarkets[submarketIndex] = {
          ...newSubMarkets[submarketIndex],
          event_image: fileUrl, // Use event_image instead of image
        };
        setSubMarkets(newSubMarkets);
      } else {
        setUploadedFiles((prev) => ({ ...prev, [field]: fileUrl }));
        validation.setFieldValue(field, fileUrl);
      }
    } catch (error) {
      const errorMessage = error.message || "Error uploading file";
      setUploadErrors((prev) => ({ ...prev, [uploadField]: errorMessage }));
      if (submarketIndex === null) {
        validation.setFieldError(field, errorMessage);
      }
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [uploadField]: false }));
    }
  };

  const formatEventData = (values) => {
    // Validate submarket fields if has_sub_markets is true
    if (hasSubMarket) {
      const invalidSubMarkets = subMarkets.filter(
        (market) =>
          !market.name ||
          !safeStringTrim(market.name) ||
          !market.start_date ||
          !market.end_date
      );

      if (invalidSubMarkets.length > 0) {
        throw new Error(
          "All submarkets must have name, start date and end date"
        );
      }
    }

    // Validate required fields with proper trimming
    const requiredFields = [
      { field: "event_title", name: "Event title" },
      { field: "market_summary", name: "Market summary" },
      { field: "rules_summary", name: "Rules summary" },
      { field: "settlement_sources", name: "Settlement sources" },
    ];

    for (const { field, name } of requiredFields) {
      if (!values[field] || !safeStringTrim(values[field])) {
        throw new Error(`${name} is required`);
      }
    }

    const eventData = {
      event_title: safeStringTrim(values.event_title),
      event_image: values.event_image,
      market_summary: safeStringTrim(values.market_summary),
      category: values.category || "660e3a12b4c5f2a1bcd24678",
      sub_category: values.sub_category || "660e3a12b4c5f2a1bcd24678",
      has_sub_markets: hasSubMarket,
      list_date: formatDateForUTC(values.list_date),
      regions: values.regions || [],
      rules_summary: safeStringTrim(values.rules_summary),
      rules_variables:
        safeStringTrim(values.rules_variables)
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v) || [],
      settlement_sources:
        safeStringTrim(values.settlement_sources)
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s) || [],
      full_rules_doc_url: values.full_rules_doc,
      total_pool_in_usd: 0,
    };

    if (hasSubMarket) {
      eventData.markets = subMarkets.map((market) => {
        // Ensure we have an image for the submarket
        if (!market.event_image && market.image) {
          market.event_image = market.image;
        }

        return {
          name: safeStringTrim(market.name),
          market_image: market.event_image,
          list_date: formatDateForUTC(market.list_date || values.list_date),
          start_date: formatDateForUTC(market.start_date),
          end_date: formatDateForUTC(market.end_date),
          yes_bids: 0,
          yes_asks: 0,
          volume: 0,
          dollar_volume: 0,
          status: "open",
          result: null,
          can_close_early: market.can_close_early,
          settlement_time: Number(market.settlement_time),
          side_1: safeStringTrim(market.side_1),
          side_2: safeStringTrim(market.side_2),
          bond_amount: Number(market.bond_amount),
          settlement_time: Number(market.settlement_time),
          resolution_window: Number(market.resolution_window),
        };
      });
    } else {
      eventData.markets = [
        {
          name: safeStringTrim(values.event_title),
          market_image: values.event_image,
          list_date: formatDateForUTC(values.list_date),
          start_date: formatDateForUTC(values.start_date),
          end_date: formatDateForUTC(values.end_date),
          yes_bids: 0,
          yes_asks: 0,
          volume: 0,
          dollar_volume: 0,
          status: "open",
          result: null,
          can_close_early: values.can_close_early,
          settlement_time: Number(values.settlement_time),
          side_1: safeStringTrim(values.side_1),
          side_2: safeStringTrim(values.side_2),
          bond_amount: Number(values.bond_amount),
          resolution_window: Number(values.resolution_window),
        },
      ];
    }

    return eventData;
  };

  // Formik setup (do NOT set initialValues from grokEvent)
  const validation = useFormik({
    enableReinitialize: false, // Set to false to prevent form reset on validation errors
    initialValues: {
      event_title: "",
      event_image: null,
      market_summary: "",
      category: "",
      sub_category: "",
      list_date: getCurrentUTCDateTimeLocal(),
      regions: [],
      rules_summary: "",
      rules_variables: "",
      settlement_sources: "",
      full_rules_doc: null,
      has_sub_markets: false,
      start_date: getCurrentUTCDateTimeLocal(),
      end_date: "",
      can_close_early: false,
      settlement_time: 24,
      side_1: "Yes",
      side_2: "No",
      bond_amount: 50,
      resolution_window: 24,
    },
    validationSchema: Yup.object({
      event_title: Yup.string()
        .transform((value) => safeStringTrim(value))
        .required("Event title is required"),
      event_image: Yup.string().required("Event image is required"),
      market_summary: Yup.string()
        .transform((value) => safeStringTrim(value))
        .required("Event summary is required"),
      category: Yup.string().required("Category is required"),
      list_date: Yup.string().required("Listing date is required"),
      rules_summary: Yup.string()
        .transform((value) => safeStringTrim(value))
        .required("Rules summary is required"),
      settlement_sources: Yup.string()
        .transform((value) => safeStringTrim(value))
        .required("Settlement sources are required"),
      full_rules_doc: Yup.string().required("Rules document is required"),
      start_date: Yup.string().when(["has_sub_markets"], {
        is: true,
        then: () => Yup.string().notRequired(),
        otherwise: () => Yup.string().required("Start date is required"),
      }),
      end_date: Yup.string().when(["has_sub_markets"], {
        is: true,
        then: () => Yup.string().notRequired(),
        otherwise: () => Yup.string().required("End date is required"),
      }),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        console.log("Form submission started with values:", values);
        console.log("hasSubMarket:", hasSubMarket);
        console.log("subMarkets:", subMarkets);

        setSubmitStatus({ loading: true, error: null, success: false });
        const eventData = formatEventData(values);
        console.log("Submitting event data:", eventData);
        const response = await createEvent(eventData);
        console.log("Event created successfully:", response);
        setSubmitStatus({ loading: false, error: null, success: true });

        // Show success message for 2 seconds then redirect
        setTimeout(() => {
          navigate("/events"); // Redirect to events list
        }, 2000);

        // Reset form after successful submission
        validation.resetForm();
        setHasSubMarket(false);
        setSubMarkets([
          {
            name: "",
            image: null,
            list_date: getCurrentUTCDateTimeLocal(),
            start_date: getCurrentUTCDateTimeLocal(),
            end_date: "",
            can_close_early: false,
            settlement_time: 24,
          },
        ]);
        setUploadedFiles({
          event_image: null,
          full_rules_doc: null,
        });
      } catch (error) {
        console.error("Error creating event:", error);
        setSubmitStatus({
          loading: false,
          error: error.message || "Failed to create event",
          success: false,
        });
        // Set form-level error if needed
        setErrors({ submit: error.message || "Failed to create event" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Helper function to check if form is ready for submission
  const isFormReadyForSubmission = () => {
    const values = validation.values;

    // Check required fields
    const requiredFields = [
      "event_title",
      "event_image",
      "market_summary",
      "category",
      "list_date",
      "rules_summary",
      "settlement_sources",
      "full_rules_doc",
    ];

    for (const field of requiredFields) {
      if (!values[field] || !safeStringTrim(values[field])) {
        console.log(`Field ${field} is missing or empty:`, values[field]);
        return false;
      }
    }

    // Check single market fields if not using sub markets
    if (!hasSubMarket) {
      if (!values.start_date || !values.end_date) {
        console.log("Single market dates missing:", {
          start_date: values.start_date,
          end_date: values.end_date,
        });
        return false;
      }
    }

    // Check sub markets if using them
    if (hasSubMarket) {
      for (let i = 0; i < subMarkets.length; i++) {
        const market = subMarkets[i];
        if (
          !market.name ||
          !safeStringTrim(market.name) ||
          !market.start_date ||
          !market.end_date
        ) {
          console.log(`Submarket ${i} is incomplete:`, market);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubMarketChange = (index, field, value) => {
    const newSubMarkets = [...subMarkets];
    newSubMarkets[index] = { ...newSubMarkets[index], [field]: value };
    setSubMarkets(newSubMarkets);
  };

  const addSubMarket = () => {
    let start_date = getCurrentUTCDateTimeLocal();
    let end_date = "";
    if (subMarkets.length > 0) {
      start_date = subMarkets[0].start_date || getCurrentUTCDateTimeLocal();
      end_date = subMarkets[0].end_date || "";
    }
    setSubMarkets([
      ...subMarkets,
      {
        name: "",
        image: null,
        list_date: getCurrentUTCDateTimeLocal(),
        start_date,
        end_date,
        can_close_early: false,
        settlement_time: 24,
        side_1: "Yes",
        side_2: "No",
        bond_amount: 50,
        settlement_time: 24,
        resolution_window: 24,
      },
    ]);
  };

  const removeSubMarket = (index) => {
    const newSubMarkets = subMarkets.filter((_, i) => i !== index);
    setSubMarkets(newSubMarkets);
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit(e);
      }}
    >
      <Card>
        <CardBody>
          {submitStatus.success && (
            <Alert color="success" className="mb-4">
              Event created successfully!
            </Alert>
          )}
          {submitStatus.error && (
            <Alert color="danger" className="mb-4">
              {submitStatus.error}
            </Alert>
          )}
          {/* Debug validation errors in development */}
          {process.env.NODE_ENV === "development" &&
            Object.keys(validation.errors).length > 0 && (
              <Alert color="warning" className="mb-4">
                <strong>Validation Errors:</strong>
                <ul className="mb-0 mt-2">
                  {Object.entries(validation.errors).map(([field, error]) => (
                    <li key={field}>
                      <strong>{field}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          <Row>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="event_title">Event Name</Label>
                <Input
                  type="text"
                  id="event_title"
                  name="event_title"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.event_title}
                  invalid={
                    validation.touched.event_title &&
                    validation.errors.event_title
                  }
                />
              </div>
            </Col>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="event_image">Event Image</Label>
                <DraggableImageUpload
                  onFileSelect={(file) => handleFileUpload(file, "event_image")}
                  currentImage={uploadedFiles.event_image}
                  isUploading={uploadingFiles.event_image}
                  uploadError={uploadErrors.event_image}
                  fieldId="event_image_upload"
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="market_summary">Event Summary</Label>
                <Input
                  type="textarea"
                  id="market_summary"
                  name="market_summary"
                  onChange={validation.handleChange}
                  value={validation.values.market_summary}
                  invalid={
                    validation.touched.market_summary &&
                    validation.errors.market_summary
                  }
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="category">Category</Label>
                <Input
                  type="select"
                  id="category"
                  name="category"
                  onChange={handleCategoryChange}
                  value={validation.values.category}
                >
                  <option value="">Select Category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category._id}>
                      {category.name}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="sub_category">Sub Category</Label>
                <Input
                  type="select"
                  id="sub_category"
                  name="sub_category"
                  onChange={validation.handleChange}
                  value={validation.values.sub_category}
                >
                  <option value="">Select Sub Category</option>
                  {subCategories.map((subCategory) => (
                    <option key={subCategory._id} value={subCategory._id}>
                      {subCategory.name}
                    </option>
                  ))}
                </Input>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="list_date">Listing Date (UTC Timezone)</Label>
                <br />
                <small className="form-text text-muted">
                  From this Date the event will be visible to users. (Select
                  time in UTC timezone)
                </small>
                <Input
                  type="datetime-local"
                  id="list_date"
                  name="list_date"
                  onChange={validation.handleChange}
                  value={validation.values.list_date}
                />
              </div>
            </Col>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="regions">Regions</Label>
                <RegionMultiSelect
                  regions={regions}
                  selectedRegions={validation.values.regions}
                  onChange={(selectedRegions) =>
                    validation.setFieldValue("regions", selectedRegions)
                  }
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="settlement_sources">Settlement Sources</Label>
                <Input
                  type="textarea"
                  id="settlement_sources"
                  name="settlement_sources"
                  onChange={validation.handleChange}
                  value={validation.values.settlement_sources}
                  placeholder="Enter settlement sources (comma separated)"
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <div className="mb-3">
                <Label htmlFor="rules_summary">Rules</Label>
                <Input
                  type="textarea"
                  id="rules_summary"
                  name="rules_summary"
                  onChange={validation.handleChange}
                  value={validation.values.rules_summary}
                />
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="full_rules_doc">Full Rules PDF</Label>
                <Input
                  type="file"
                  id="full_rules_doc"
                  name="full_rules_doc"
                  onChange={(e) =>
                    handleFileUpload(e.target.files[0], "full_rules_doc")
                  }
                  accept=".pdf"
                  disabled={uploadingFiles.full_rules_doc}
                />
                {uploadingFiles.full_rules_doc && (
                  <div className="mt-2">
                    <Spinner size="sm" /> Uploading...
                  </div>
                )}
                {uploadErrors.full_rules_doc && (
                  <Alert color="danger" className="mt-2">
                    {uploadErrors.full_rules_doc}
                  </Alert>
                )}
                {uploadedFiles.full_rules_doc && (
                  <div className="mt-2">
                    <Alert color="success">
                      PDF uploaded successfully!{" "}
                      <a
                        href={uploadedFiles.full_rules_doc}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View PDF
                      </a>
                    </Alert>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <div className="mb-3">
                <Label className="d-block mb-2" htmlFor="has_sub_markets">
                  Has Multiple Markets
                </Label>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Switch
                    checked={hasSubMarket}
                    onChange={(checked) => {
                      console.log(
                        "Multiple Markets toggle switched to:",
                        checked
                      );
                      setHasSubMarket(checked);
                      validation.setFieldValue("has_sub_markets", checked);
                      if (checked) {
                        // When switching ON, preserve start_date and end_date from form if filled
                        setSubMarkets((prev) => {
                          const mainStart = validation.values.start_date;
                          const mainEnd = validation.values.end_date;
                          return [
                            {
                              name: "",
                              image: null,
                              list_date: getCurrentUTCDateTimeLocal(),
                              start_date:
                                mainStart || getCurrentUTCDateTimeLocal(),
                              end_date: mainEnd || "",
                              can_close_early: false,
                              settlement_time: 24,
                            },
                          ];
                        });
                      }
                    }}
                    onColor="#86d3ff"
                    onHandleColor="#2693e6"
                    offColor="#cccccc"
                    offHandleColor="#888888"
                    handleDiameter={24}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    height={20}
                    width={48}
                    id="has_sub_markets"
                    className="me-2"
                  />
                  <span>{hasSubMarket ? "Yes" : "No"}</span>
                </div>
              </div>
            </Col>
          </Row>

          {hasSubMarket ? (
            <div>
              {subMarkets.map((subMarket, index) => (
                <Card key={index} className="mb-3">
                  <CardBody>
                    <Row>
                      <Col lg="11">
                        <h5>Sub Market {index + 1}</h5>
                      </Col>
                      <Col lg="1">
                        {index > 0 && (
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => removeSubMarket(index)}
                          >
                            ✕
                          </Button>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label>Submarket Name</Label>
                          <Input
                            type="text"
                            value={subMarket.name}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </Col>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label>Submarket Image</Label>
                          <DraggableImageUpload
                            onFileSelect={(file) =>
                              handleFileUpload(file, "image", index)
                            }
                            currentImage={subMarket.event_image}
                            isUploading={uploadingFiles[`submarket_${index}`]}
                            uploadError={uploadErrors[`submarket_${index}`]}
                            fieldId={`submarket_image_${index}`}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      {/* <Col lg="4">
                        <div className="mb-3">
                          <Label>List Date</Label>
                          <Input
                            type="datetime-local"
                            value={subMarket.list_date}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "list_date",
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </Col> */}
                      <Col lg="6">
                        <div className="mb-3">
                          <Label>Start Date (UTC Timezone)</Label>
                          <br />
                          <small className="form-text text-muted">
                            From this Date the users can place orders. (Select
                            time in UTC timezone)
                          </small>
                          <Input
                            type="datetime-local"
                            value={
                              subMarket.start_date
                                ? normalizeDateTimeLocal(subMarket.start_date)
                                : ""
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              handleSubMarketChange(
                                index,
                                "start_date",
                                val ? normalizeDateTimeLocal(val) : ""
                              );
                            }}
                          />
                        </div>
                      </Col>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label>End Date (UTC Timezone)</Label>
                          <br />
                          <small className="form-text text-muted">
                            From this Date the users can not place orders.
                            (Select time in UTC timezone)
                          </small>
                          <Input
                            type="datetime-local"
                            value={
                              subMarket.end_date
                                ? normalizeDateTimeLocal(subMarket.end_date)
                                : ""
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              handleSubMarketChange(
                                index,
                                "end_date",
                                val ? normalizeDateTimeLocal(val) : ""
                              );
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label className="d-block mb-2">
                            Can close early?
                          </Label>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Switch
                              checked={subMarket.can_close_early}
                              onChange={(checked) => {
                                console.log(
                                  "Submarket toggle switched to:",
                                  checked
                                );
                                handleSubMarketChange(
                                  index,
                                  "can_close_early",
                                  checked
                                );
                              }}
                              onColor="#86d3ff"
                              onHandleColor="#2693e6"
                              offColor="#cccccc"
                              offHandleColor="#888888"
                              handleDiameter={24}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              height={20}
                              width={48}
                              className="me-2"
                            />
                            <span>
                              {subMarket.can_close_early ? "Yes" : "No"}
                            </span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label>Side 1</Label>
                          <Input
                            type="text"
                            value={subMarket.side_1}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "side_1",
                                e.target.value
                              )
                            }
                            placeholder="Enter Side 1 description"
                          />
                        </div>
                      </Col>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label>Side 2</Label>
                          <Input
                            type="text"
                            value={subMarket.side_2}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "side_2",
                                e.target.value
                              )
                            }
                            placeholder="Enter Side 2 description"
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="4">
                        <div className="mb-3">
                          <Label>Bond Amount ($)</Label>
                          <Input
                            type="number"
                            value={subMarket.bond_amount}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "bond_amount",
                                Number(e.target.value)
                              )
                            }
                            min="0"
                            placeholder="Bond amount in USD"
                          />
                        </div>
                      </Col>
                      <Col lg="4">
                        <div className="mb-3">
                          <Label>Settlement Time (hours)</Label>
                          <Input
                            type="number"
                            value={subMarket.settlement_time}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "settlement_time",
                                Number(e.target.value)
                              )
                            }
                            min="1"
                            placeholder="Settlement time in hours"
                          />
                        </div>
                      </Col>
                      <Col lg="4">
                        <div className="mb-3">
                          <Label>Resolution Window (hours)</Label>
                          <Input
                            type="number"
                            value={subMarket.resolution_window}
                            onChange={(e) =>
                              handleSubMarketChange(
                                index,
                                "resolution_window",
                                Number(e.target.value)
                              )
                            }
                            min="1"
                            placeholder="Resolution window in hours"
                          />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
              <Button color="primary" onClick={addSubMarket} className="mb-3">
                Add Submarket
              </Button>
            </div>
          ) : (
            <div>
              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="start_date">
                      Start Date (UTC Timezone)
                    </Label>
                    <br />
                    <small className="form-text text-muted">
                      From this Date the users can place orders. (Select time in
                      UTC timezone)
                    </small>
                    <Input
                      type="datetime-local"
                      id="start_date"
                      name="start_date"
                      onChange={(e) => {
                        const val = e.target.value;
                        validation.setFieldValue(
                          "start_date",
                          val ? normalizeDateTimeLocal(val) : ""
                        );
                      }}
                      onBlur={validation.handleBlur}
                      value={
                        validation.values.start_date
                          ? normalizeDateTimeLocal(validation.values.start_date)
                          : ""
                      }
                      invalid={
                        validation.touched.start_date &&
                        validation.errors.start_date &&
                        !validation.values.has_sub_markets
                      }
                    />
                    {validation.touched.start_date &&
                      validation.errors.start_date &&
                      !validation.values.has_sub_markets && (
                        <div className="text-danger">
                          {validation.errors.start_date}
                        </div>
                      )}
                  </div>
                </Col>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="end_date">End Date (UTC Timezone)</Label>
                    <br />
                    <small className="form-text text-muted">
                      From this Date the users can not place orders. (Select
                      time in UTC timezone)
                    </small>
                    <Input
                      type="datetime-local"
                      id="end_date"
                      name="end_date"
                      onChange={(e) => {
                        const val = e.target.value;
                        validation.setFieldValue(
                          "end_date",
                          val ? normalizeDateTimeLocal(val) : ""
                        );
                      }}
                      onBlur={validation.handleBlur}
                      value={
                        validation.values.end_date
                          ? normalizeDateTimeLocal(validation.values.end_date)
                          : ""
                      }
                      invalid={
                        validation.touched.end_date &&
                        validation.errors.end_date &&
                        !validation.values.has_sub_markets
                      }
                    />
                    {validation.touched.end_date &&
                      validation.errors.end_date &&
                      !validation.values.has_sub_markets && (
                        <div className="text-danger">
                          {validation.errors.end_date}
                        </div>
                      )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label className="d-block mb-2" htmlFor="can_close_early">
                      Can close early?
                    </Label>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Switch
                        checked={validation.values.can_close_early}
                        onChange={(checked) => {
                          console.log(
                            "Can close early toggle switched to:",
                            checked
                          );
                          validation.setFieldValue("can_close_early", checked);
                        }}
                        onColor="#86d3ff"
                        onHandleColor="#2693e6"
                        offColor="#cccccc"
                        offHandleColor="#888888"
                        handleDiameter={24}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        height={20}
                        width={48}
                        id="can_close_early_switch"
                        className="me-2"
                      />
                      <span>
                        {validation.values.can_close_early ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="side_1">Side 1</Label>
                    <Input
                      type="text"
                      id="side_1"
                      name="side_1"
                      onChange={validation.handleChange}
                      value={validation.values.side_1}
                      placeholder="Enter Side 1 description"
                    />
                  </div>
                </Col>
                <Col lg="6">
                  <div className="mb-3">
                    <Label htmlFor="side_2">Side 2</Label>
                    <Input
                      type="text"
                      id="side_2"
                      name="side_2"
                      onChange={validation.handleChange}
                      value={validation.values.side_2}
                      placeholder="Enter Side 2 description"
                    />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg="4">
                  <div className="mb-3">
                    <Label htmlFor="bond_amount">Bond Amount ($)</Label>
                    <Input
                      type="number"
                      id="bond_amount"
                      name="bond_amount"
                      onChange={validation.handleChange}
                      value={validation.values.bond_amount}
                      min="0"
                      placeholder="Bond amount in USD"
                    />
                  </div>
                </Col>
                <Col lg="4">
                  <div className="mb-3">
                    <Label htmlFor="settlement_time_new">
                      Settlement Time (hours)
                    </Label>
                    <Input
                      type="number"
                      id="settlement_time_new"
                      name="settlement_time"
                      onChange={validation.handleChange}
                      value={validation.values.settlement_time}
                      min="1"
                      placeholder="Settlement time in hours"
                    />
                  </div>
                </Col>
                <Col lg="4">
                  <div className="mb-3">
                    <Label htmlFor="resolution_window">
                      Resolution Window (hours)
                    </Label>
                    <Input
                      type="number"
                      id="resolution_window"
                      name="resolution_window"
                      onChange={validation.handleChange}
                      value={validation.values.resolution_window}
                      min="1"
                      placeholder="Resolution window in hours"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}

          <Row>
            <Col>
              <div className="text-end">
                <Button
                  type="submit"
                  color="primary"
                  disabled={
                    submitStatus.loading ||
                    Object.values(uploadingFiles).some(Boolean) ||
                    !isFormReadyForSubmission()
                  }
                >
                  {submitStatus.loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Creating Event...
                    </>
                  ) : Object.values(uploadingFiles).some(Boolean) ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Uploading...
                    </>
                  ) : !isFormReadyForSubmission() ? (
                    "Please fill all required fields"
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      {onBack && (
        <Button color="secondary" onClick={onBack} className="mb-3">
          Back to Events List
        </Button>
      )}
    </Form>
  );
};

// Assign to variable before export for ESLint
const NewEventComponent = NewEvent;
export default NewEventComponent;
